import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Shield } from "lucide-react";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Polityka Prywatności - GdzieSchronisko</title>
        <meta name="description" content="Polityka prywatności serwisu GdzieSchronisko. Informacje o przetwarzaniu danych osobowych, plikach cookies i prawach użytkowników." />
        <link rel="canonical" href="https://gdzieschronisko.pl/polityka-prywatnosci" />
      </Helmet>
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 sm:px-6">
        <PageBreadcrumbs currentPage="Polityka prywatności" />

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-3xl font-display font-bold">Polityka prywatności</h1>
        </div>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Administrator danych</h2>
            <p className="text-muted-foreground leading-relaxed">
              Administratorem danych osobowych jest osoba fizyczna prowadząca serwis GdzieSchronisko 
              jako niezależny, niekomercyjny projekt informacyjny. 
              Kontakt z administratorem możliwy jest pod adresem e-mail: <strong>kontakt@gdzieschronisko.pl</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Jakie dane zbieramy</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              W ramach korzystania z serwisu GdzieSchronisko możemy zbierać następujące dane:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Dane lokalizacyjne</strong> – za Twoją zgodą, w celu wyświetlenia najbliższych schronisk. Dane te nie są zapisywane na serwerze, a jedynie przetwarzane lokalnie w Twojej przeglądarce.</li>
              <li><strong>Dane z formularzy</strong> – nazwa schroniska, adres, dane kontaktowe przy zgłaszaniu aktualizacji</li>
              <li><strong>Dane techniczne</strong> – adres IP, typ przeglądarki, czas wizyty (standardowe logi serwera)</li>
              <li><strong>Pliki cookies</strong> – informacja o wyrażonej zgodzie na cookies (przechowywana lokalnie w przeglądarce)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Cel przetwarzania danych</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Twoje dane przetwarzamy w celu:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Wyświetlania schronisk posortowanych według odległości od Twojej lokalizacji</li>
              <li>Umożliwienia zgłaszania nowych schronisk i aktualizacji informacji</li>
              <li>Odpowiadania na wiadomości przesłane przez formularz kontaktowy</li>
              <li>Zapewnienia bezpieczeństwa i prawidłowego działania serwisu</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Podstawa prawna przetwarzania</h2>
            <p className="text-muted-foreground leading-relaxed">
              Dane przetwarzamy na podstawie:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
              <li><strong>Art. 6 ust. 1 lit. a RODO</strong> – Twoja zgoda (np. na geolokalizację, cookies)</li>
              <li><strong>Art. 6 ust. 1 lit. f RODO</strong> – prawnie uzasadniony interes administratora (bezpieczeństwo serwisu)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Okres przechowywania danych</h2>
            <p className="text-muted-foreground leading-relaxed">
              Dane przechowujemy przez okres niezbędny do realizacji celów, dla których zostały zebrane, 
              lub do momentu wycofania zgody. Zgłoszenia schronisk przechowywane są do czasu ich weryfikacji 
              i ewentualnego dodania do bazy. Standardowe logi serwera przechowywane są przez 14 dni.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. Twoje prawa</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Zgodnie z RODO przysługuje Ci prawo do:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Dostępu do swoich danych osobowych</li>
              <li>Sprostowania (poprawienia) danych</li>
              <li>Usunięcia danych („prawo do bycia zapomnianym")</li>
              <li>Ograniczenia przetwarzania</li>
              <li>Przenoszenia danych</li>
              <li>Wniesienia sprzeciwu wobec przetwarzania</li>
              <li>Cofnięcia zgody w dowolnym momencie (np. poprzez wyczyszczenie cookies lub odmowę geolokalizacji)</li>
              <li>Wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (UODO)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Aby skorzystać z powyższych praw, skontaktuj się z nami pod adresem: <strong>kontakt@gdzieschronisko.pl</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Pliki cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Serwis wykorzystuje pliki cookies w następujących celach:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Cookies niezbędne</strong> – zapamiętanie Twojej zgody na cookies</li>
              <li><strong>Cookies funkcjonalne</strong> – zapamiętanie preferencji (np. wybranego miasta)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Możesz zarządzać cookies w ustawieniach przeglądarki lub klikając „Zarządzaj cookies" w stopce strony.
              Nie używamy cookies analitycznych ani reklamowych podmiotów trzecich.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Odbiorcy danych</h2>
            <p className="text-muted-foreground leading-relaxed">
              Dane mogą być przekazywane wyłącznie podmiotom świadczącym usługi hostingowe niezbędne 
              do działania serwisu. Nie sprzedajemy ani nie udostępniamy danych osobowych podmiotom trzecim 
              w celach marketingowych.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Bezpieczeństwo danych</h2>
            <p className="text-muted-foreground leading-relaxed">
              Stosujemy odpowiednie środki techniczne i organizacyjne w celu ochrony Twoich danych osobowych 
              przed nieuprawnionym dostępem, utratą lub zniszczeniem. Połączenie z serwisem jest szyfrowane 
              protokołem HTTPS.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Zmiany w polityce prywatności</h2>
            <p className="text-muted-foreground leading-relaxed">
              Zastrzegamy sobie prawo do wprowadzania zmian w niniejszej polityce prywatności. 
              O istotnych zmianach będziemy informować poprzez aktualizację treści na tej stronie.
            </p>
          </section>

          <section className="bg-muted/50 p-6 rounded-xl border border-border">
            <p className="text-sm text-muted-foreground">
              <strong>Data ostatniej aktualizacji:</strong> 30 stycznia 2025
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              W razie pytań dotyczących polityki prywatności, prosimy o kontakt: 
              <a href="mailto:kontakt@gdzieschronisko.pl" className="text-primary hover:underline ml-1">
                kontakt@gdzieschronisko.pl
              </a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
